import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import {
  BRANCH_HEADER,
  IDEMPOTENCY_KEY_HEADER,
  REQUEST_ID_HEADER,
  type ApiErrorDetail,
  type ApiErrorResponse,
  type ApiSuccessResponse,
  type LoginResponse,
} from '@optiflow/types';
import { authStore } from '@/store/authStore';

/**
 * Where the API is, from the page's point of view.
 *
 * Same origin by default: `/api/v1`, resolved against whatever origin served the page. The dev server
 * proxies `/api` to the API on the same machine, and production serves the app and the API under one
 * origin too, so the browser never needs to know the API's own port or host. That is what lets one
 * build work unchanged from `http://localhost`, from a LAN address over HTTPS, and from a phone —
 * on which `localhost` would mean the phone.
 *
 * An absolute `VITE_API_URL` is still honoured, for a deployment that genuinely serves the API from a
 * different origin. It is an explicit choice, never a fallback.
 */
export const SAME_ORIGIN_API_BASE = '/api/v1';

export function resolveApiBaseUrl(configured: string | undefined): string {
  const value = configured?.trim();
  return value ? value.replace(/\/+$/, '') : SAME_ORIGIN_API_BASE;
}

const BASE_URL = resolveApiBaseUrl(import.meta.env.VITE_API_URL);

/** For requests that cannot go through axios, such as the session status stream. */
export const API_BASE_URL = BASE_URL;

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
});

/** A failed request that should be replayed once the access token has been renewed. */
type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken, activeBranchId } = authStore.get();

  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }

  // Only ever a request. The API confirms the caller may act on this branch.
  //
  // An explicit branch already on the request wins. A screen that asks which branch a document
  // belongs to has to be able to say so for that one request, without switching the branch
  // everyone else in the tab is working in.
  if (activeBranchId && !config.headers.has(BRANCH_HEADER)) {
    config.headers.set(BRANCH_HEADER, activeBranchId);
  }

  // One id per HTTP attempt — deliberately not the idempotency key, which is stable across the
  // retries of a single business intent. A retry therefore carries the same key and a new id, which
  // is exactly what makes a support conversation about "which attempt" possible.
  config.headers.set(REQUEST_ID_HEADER, crypto.randomUUID());

  return config;
});

/** The request id the API answered with, kept so a failure can be quoted to support. */
let lastRequestId: string | null = null;

apiClient.interceptors.response.use(
  (response) => {
    const id = response.headers?.[REQUEST_ID_HEADER];
    if (typeof id === 'string') lastRequestId = id;
    return response;
  },
  (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const id = error.response?.headers?.[REQUEST_ID_HEADER];
      if (typeof id === 'string') lastRequestId = id;
    }
    return Promise.reject(error);
  },
);

/**
 * One shared refresh attempt.
 *
 * Without this, a page that fires several queries at once would send several refresh requests with
 * the same token — and because refresh tokens rotate, the later ones would look like a replay and
 * the API would (correctly) end the session.
 */
let refreshInFlight: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const { refreshToken } = authStore.get();
  if (!refreshToken) throw new Error('No refresh token available.');

  // A bare axios call, so this request never re-enters the interceptor that called it.
  const response = await axios.post<ApiSuccessResponse<LoginResponse>>(
    `${BASE_URL}/auth/refresh`,
    { refreshToken },
    { headers: { 'Content-Type': 'application/json' }, timeout: 20_000 },
  );

  const { user, tokens } = response.data.data;
  authStore.get().setSession(user, tokens);

  return tokens.accessToken;
}

/** The single shared refresh, for callers outside axios too — a second concurrent refresh would look like a replayed token. */
export function refreshAccessTokenOnce(): Promise<string> {
  refreshInFlight ??= refreshAccessToken().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorResponse>) => {
    const config = error.config as RetriableConfig | undefined;
    const status = error.response?.status;
    const isAuthEndpoint = config?.url?.includes('/auth/');

    if (status !== 401 || !config || config._retried || isAuthEndpoint) {
      return Promise.reject(error);
    }

    config._retried = true;

    try {
      const accessToken = await refreshAccessTokenOnce();
      config.headers.set('Authorization', `Bearer ${accessToken}`);

      return await apiClient.request(config);
    } catch {
      authStore.get().clear();
      return Promise.reject(error);
    }
  },
);

/** Unwraps the API's success envelope so call sites deal only in payloads. */
async function unwrap<T>(request: Promise<{ data: ApiSuccessResponse<T> }>): Promise<T> {
  const response = await request;
  return response.data.data;
}

export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    unwrap<T>(apiClient.get<ApiSuccessResponse<T>>(url, config)),

  post: <T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    unwrap<T>(apiClient.post<ApiSuccessResponse<T>>(url, body, config)),

  patch: <T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    unwrap<T>(apiClient.patch<ApiSuccessResponse<T>>(url, body, config)),

  /** For a record-or-replace, where sending the same body twice must mean the same thing. */
  put: <T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    unwrap<T>(apiClient.put<ApiSuccessResponse<T>>(url, body, config)),

  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    unwrap<T>(apiClient.delete<ApiSuccessResponse<T>>(url, config)),

  /** For endpoints that return no envelope, such as `/health`. */
  raw: apiClient,
};

/** Pulls the machine-readable error code out of a failed request. */
export function apiErrorCode(error: unknown): string | null {
  if (axios.isAxiosError<ApiErrorResponse>(error)) {
    return error.response?.data?.error?.code ?? null;
  }
  return null;
}

export function apiErrorMessage(error: unknown): string | null {
  if (axios.isAxiosError<ApiErrorResponse>(error)) {
    return error.response?.data?.error?.message ?? null;
  }
  return null;
}

/** The per-field details of a failed request, such as which basket line a refusal names; empty when there are none. */
export function apiErrorDetails(error: unknown): ApiErrorDetail[] {
  if (axios.isAxiosError<ApiErrorResponse>(error)) {
    return error.response?.data?.error?.details ?? [];
  }
  return [];
}

/**
 * Whether the request never completed a round trip.
 *
 * Worth its own function because the distinction is invisible otherwise: both a server rejection
 * and a dead connection leave `apiErrorMessage` returning null, so without this the counter cannot
 * tell "that product is discontinued" from "the shop's internet died" — and those want opposite
 * responses from the cashier.
 *
 * It is also what decides whether an idempotency key goes back with the retry: a request that never
 * arrived may still have been acted on.
 */
export function isNetworkError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response) return false;

  return (
    error.code === 'ECONNABORTED' ||
    error.code === 'ERR_NETWORK' ||
    error.code === 'ETIMEDOUT' ||
    error.message === 'Network Error' ||
    error.code === undefined
  );
}

/** The id of the most recent response, for an error message the counter can quote. */
export function lastResponseRequestId(): string | null {
  return lastRequestId;
}

/**
 * Names the branch one request acts in, overriding the ambient active branch.
 *
 * For screens where the branch is part of what is being decided rather than the context it happens
 * in — recording an expense at a branch you are not currently looking at, for instance.
 */
export function withBranch(branchId: string, config: AxiosRequestConfig = {}): AxiosRequestConfig {
  return {
    ...config,
    headers: { ...(config.headers ?? {}), [BRANCH_HEADER]: branchId },
  };
}

/** Attaches an idempotency key to a mutating request. */
export function withIdempotencyKey(
  key: string,
  config: AxiosRequestConfig = {},
): AxiosRequestConfig {
  return {
    ...config,
    headers: { ...(config.headers ?? {}), [IDEMPOTENCY_KEY_HEADER]: key },
  };
}
